import React from "react";
import { Link } from "react-router-dom";
import { Sprout, Target, Eye, Heart, Truck, Users, ShieldCheck, TrendingUp } from "lucide-react";
import SeoHead from "@/components/seo/SeoHead";

const About = () => (
  <div data-testid="page-about">
    <SeoHead
      title="About Us — Our Story & Mission"
      description="FarmersAgroHub is India's trusted multi-vendor agriculture marketplace connecting farmers with GST-verified sellers across 18,000+ pincodes. Learn about our mission to empower Indian agriculture."
      path="/about"
    />
    {/* HERO */}
    <section className="bg-primary text-white py-16 sm:py-24">
      <div className="container-fah grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-3 py-1 text-xs font-semibold mb-4">
            <Sprout className="w-3.5 h-3.5" /> About FarmersAgroHub
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-tight text-balance">
            Empowering Indian Agriculture, <span className="text-secondary">One Farm at a Time</span>
          </h1>
          <p className="mt-5 text-white/85 text-lg max-w-xl">
            Born in 2024, FarmersAgroHub is on a mission to make quality agri-inputs accessible, affordable & transparent for every Indian farmer — from Punjab fields to Tamil Nadu groves.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Stat n="5,000+" l="Verified Sellers" />
          <Stat n="18,000+" l="Pincodes Served" />
          <Stat n="1.2L+" l="Happy Farmers" />
          <Stat n="₹50Cr+" l="Saved in Costs" />
        </div>
      </div>
    </section>

    {/* MISSION / VISION */}
    <section className="container-fah py-16">
      <div className="grid md:grid-cols-3 gap-6">
        <Pillar Icon={Target} title="Our Mission" body="To empower Indian agriculture through technology and trusted agricultural products — making genuine seeds, crop protection, machinery & expertise available to every farmer." />
        <Pillar Icon={Eye} title="Our Vision" body="A future where every Indian farmer has equal access to the best inputs, fairest prices, and modern knowledge — regardless of geography or scale." />
        <Pillar Icon={Heart} title="Our Values" body="Trust through verification. Transparency in pricing. Empathy in support. Innovation in service. Bharat at the heart of every decision." />
      </div>
    </section>

    {/* WHY US */}
    <section className="bg-muted/40 py-16">
      <div className="container-fah">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-[0.15em] text-primary font-semibold mb-2">Why FarmersAgroHub</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold">Built different. Built for Bharat.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { I: ShieldCheck, t: "GST Verified Sellers", s: "Every seller verified — no fakes, no fraud." },
            { I: Truck, t: "Pan-India Logistics", s: "From Leh to Kanyakumari — we deliver everywhere." },
            { I: Users, t: "Multi-Vendor Marketplace", s: "Compare prices across thousands of sellers." },
            { I: TrendingUp, t: "Fair Pricing", s: "Cut middlemen. Direct from manufacturer to farm." },
          ].map(({ I, t, s }, i) => (
            <div key={i} className="card-fah p-6">
              <div className="w-12 h-12 rounded-xl bg-accent text-primary flex items-center justify-center mb-3"><I className="w-6 h-6" /></div>
              <h3 className="font-display font-bold">{t}</h3>
              <p className="text-sm text-muted-foreground mt-1.5">{s}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* FUTURE */}
    <section className="container-fah py-16">
      <div className="text-center mb-10">
        <div className="text-xs uppercase tracking-[0.15em] text-primary font-semibold mb-2">What's Next</div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold">Coming soon to FarmersAgroHub</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
        {[
          { e: "🌾", t: "AI Crop Advisory" },
          { e: "👨‍🌾", t: "Farmer Community" },
          { e: "📰", t: "Agri News" },
          { e: "☀️", t: "Weather Updates" },
          { e: "🔬", t: "Disease Detection" },
        ].map((x, i) => (
          <div key={i} className="card-fah p-5 text-center hover-lift">
            <div className="text-3xl mb-2">{x.e}</div>
            <div className="text-sm font-semibold">{x.t}</div>
          </div>
        ))}
      </div>
    </section>

    {/* CTA */}
    <section className="container-fah pb-20">
      <div className="card-fah p-8 sm:p-12 text-center bg-gradient-to-br from-primary to-primary-hover text-white border-0">
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold">Ready to join the agri revolution?</h2>
        <p className="mt-3 text-white/85 max-w-xl mx-auto">Whether you're a farmer or an agri-business, FarmersAgroHub is your partner in growth.</p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link to="/register" className="bg-secondary text-secondary-foreground font-bold py-3 px-6 rounded-lg">Shop as Farmer</Link>
          <Link to="/seller/register" className="bg-white text-primary font-bold py-3 px-6 rounded-lg">Become a Seller</Link>
        </div>
      </div>
    </section>
  </div>
);

const Stat = ({ n, l }) => (
  <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/15">
    <div className="font-display font-extrabold text-2xl text-secondary">{n}</div>
    <div className="text-xs text-white/80 mt-1">{l}</div>
  </div>
);

const Pillar = ({ Icon, title, body }) => (
  <div className="card-fah p-7 hover-lift">
    <div className="w-12 h-12 rounded-xl bg-accent text-primary flex items-center justify-center mb-4"><Icon className="w-6 h-6" /></div>
    <h3 className="font-display font-bold text-xl">{title}</h3>
    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{body}</p>
  </div>
);

export default About;
